<template>
  <AUpload
    ref="uploadRef"
    class="n-image-upload"
    :capture="null"
    :class="{ 'c-rounded': rounded }"
    :disabled="disabled"
    :open-file-dialog-on-click="!hasImage"
    :before-upload="onBeforeUpload"
    v-bind="attrs"
    @change="onChange"
  >
    <!-- preview -->
    <div class="c-container">
      <div class="c-preview" :style="previewStyle" />
      <div v-if="!hasImage && !mLoading" class="c-plus-icon text-center">
        <i class="fas fa-plus" />
        <div class="text-[10px] scale-0.5">{{ uploadLimitation }}</div>
      </div>
      <div v-if="mLoading" class="c-loading">
        <i class="fas fa-spinner n-spinner" />
      </div>
      <div v-if="hasImage" class="c-mask">
        <div class="c-action">
          <i class="c-icon fas fa-eye cursor-pointer" @click.stop="onPreview()" />
          <template v-if="!disabled">
            <span class="m-3">|</span>
            <i class="c-icon fas fa-trash-alt cursor-pointer" @click.stop="onRemove()" />
          </template>
        </div>
      </div>
    </div>
    <AModal v-model:visible="previewModalVisible" :width="700" :footer="null">
      <img alt="" style="width: 100%" :src="file?.path" />
    </AModal>
  </AUpload>
</template>

<script setup>
import {ErrorMessage} from '@/models';
import {useBootstrap, useLanguage, useValidate} from '@front/hooks';
import {createResponseErrorMessage, errorHandler} from '@/services';
import {FILE_TYPE, MAX_UPLOAD_FILE_SIZE} from '@/constant';
import {computed, reactive, ref, useAttrs, watchEffect} from 'vue';
import {Modal as AModal, Upload as AUpload} from 'ant-design-vue/es';

const props = defineProps({
  file: Object,
  loading: Boolean,
  status: String,
  disabled: Boolean,
  type: { type: String, validator: v => ['ID', 'BANK_CARD', 'TRANSFER_PROOF'].includes(v) },
  onChange: Function,
  beforeUpload: Function,
  rounded: Boolean,
});

const emits = defineEmits(['update:file', 'update:loading', 'update:status', 'change']);

const { token } = useBootstrap();

const { t, tl, PREFIX } = useLanguage();

const { checkFileSuffixAndSize } = useValidate();

const previewStyle = reactive({ backgroundImage: null });

watchEffect(() => {
  previewStyle.backgroundImage = props.file?.path ? `url(${props.file?.path})` : undefined;
});

const uploadLimitation = computed(() => {
  const types = Object.keys(FILE_TYPE)
    .filter(key => [FILE_TYPE.JPG, FILE_TYPE.PNG].includes(FILE_TYPE[key]))
    .join('/');
  return tl('uploadLimitation', { types, maxSize: MAX_UPLOAD_FILE_SIZE });
});

const attrs = computed(() => ({
  showUploadList: false,
  action: '/api/front/resource',
  headers: {
    token: token.value,
  },
  data: {
    type: props.type,
  },
  name: 'file',
  // ...useAttrs(),
}));

const hasImage = computed(() => props.file?.path);

const innerLoading = ref(false);

const mLoading = computed(() => props.loading || innerLoading.value);

const previewModalVisible = ref(false);

const uploadRef = ref();

const onError = response => {
  innerLoading.value = false;
  emits('update:loading', false);
  emits('update:status', 'error');
  if (response) {
    throw createResponseErrorMessage(response);
  } else {
    throw new ErrorMessage({ content: t('uploadFailed', PREFIX.ERROR) });
  }
};

function onBeforeUpload(event) {
  const beforeUpload = props.beforeUpload || checkFileSuffixAndSize;
  try {
    beforeUpload(event);
  } catch (error) {
    // 当throw error时, input的值是选中的文件值, 这点不符合预期
    // 总体来说是antdv的底层对error处理的不好 [如果是promise reject, 则直接吃掉, 外部无法判断]
    uploadRef.value.$el.querySelector('input').value = '';
    errorHandler(error);
    return false;
  }
  return true;
}

const onChange = info => {
  try {
    emits('change', info);
    if (info.file.status === 'uploading') {
      innerLoading.value = true;
      emits('update:loading', true);
      emits('update:status', 'uploading');
      return;
    } else if (info.file.status === 'done') {
      innerLoading.value = false;
      emits('update:loading', false);
      emits('update:status', 'done');
      const { response } = info.file;
      const { success } = response;
      if (success === false) {
        emits('update:file', null);
        onError(response);
      } else {
        emits('update:file', response);
      }
    } else if (info.file.status === 'error') {
      innerLoading.value = false;
      emits('update:loading', false);
      emits('update:status', 'done');
      emits('update:file', null);
      onError();
    }
  } catch (error) {
    errorHandler(error);
  }
};

const onPreview = () => (previewModalVisible.value = true);

const onRemove = () => {
  emits('update:file', null);
  previewStyle.backgroundImage = '';
  // TODO: 第一期选不做真正删除, (有问题的场景: 编辑身份验证图片(假设有2两图), 删除一个, 文件真正删除, 但若不更新identification信息, 直接刷新, 则找不到对应图片了)
  // return remove(props.file.path).finally(() => {
  //   emits('update:file', null);
  //   previewStyle.backgroundImage = '';
  // });
};
</script>

<script>
export default {
  name: 'NImageUpload',
  // inheritAttrs: false,
};
</script>

<style lang="less">
.n-image-upload {
  display: inline-block;

  .c-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 120px;
    height: 120px;
    background-color: #efefef;
    border: 1px dotted gray;
    border-radius: 10px;
    position: relative;

    .c-preview {
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      left: 5px;
      right: 5px;
      top: 5px;
      bottom: 5px;
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
      border-radius: 10px;
    }

    .c-plus-icon,
    .c-loading {
      font-size: 30px;
      color: #555;
    }

    .c-plus-icon {
      width: 100%;
      white-space: pre-wrap;
    }

    .c-mask {
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      left: 5px;
      right: 5px;
      top: 5px;
      bottom: 5px;
      background-color: black;
      border-radius: 10px;
      @apply opacity-50 md:opacity-0;

      &:hover {
        opacity: 0.5;
      }
    }

    .c-action {
      font-size: 25px;
      color: #ccc;
      .c-icon:hover {
        color: white;
      }
    }
  }

  &.c-rounded {
    .c-container {
      border-radius: 100%;
      .c-preview {
        border-radius: 100%;
      }
      .c-mask {
        border-radius: 100%;
      }
    }
  }

  .ant-upload-disabled {
    .c-plus-icon,
    .c-loading {
      color: #aaa !important;
    }
  }
}
</style>
